import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SkinId } from '../../features/game/components/board/skins/types';

export type { SkinId };

export type SkinComponentType = 'pawn' | 'dice' | 'base' | 'plateaux';

interface SkinState {
  pawnSkinId: SkinId;
  diceSkinId: SkinId;
  baseSkinId: SkinId;
  plateauxSkinId: SkinId;
  /** Skins currently being trialed (usable for one game) */
  activeTrialSkinIds: SkinId[];
  /** Skins whose trial has been consumed (played a game) */
  consumedTrialSkinIds: SkinId[];
  setComponentSkin: (component: SkinComponentType, skinId: SkinId) => void;
  setAllSkins: (skinId: SkinId) => void;
  startTrial: (skinId: SkinId) => void;
  consumeTrials: () => void;
}

export const useSkinStore = create<SkinState>()(
  persist(
    (set, get) => ({
      pawnSkinId: 'basics',
      diceSkinId: 'basics',
      baseSkinId: 'basics',
      plateauxSkinId: 'basics',
      activeTrialSkinIds: [],
      consumedTrialSkinIds: [],
      setComponentSkin: (component, skinId) => {
        switch (component) {
          case 'pawn':
            set({ pawnSkinId: skinId });
            break;
          case 'dice':
            set({ diceSkinId: skinId });
            break;
          case 'base':
            set({ baseSkinId: skinId });
            break;
          case 'plateaux':
            set({ plateauxSkinId: skinId });
            break;
        }
      },
      setAllSkins: (skinId) => {
        set({
          pawnSkinId: skinId,
          diceSkinId: skinId,
          baseSkinId: skinId,
          plateauxSkinId: skinId
        });
      },
      startTrial: (skinId) => {
        const { activeTrialSkinIds } = get();
        if (!activeTrialSkinIds.includes(skinId)) {
          set({ activeTrialSkinIds: [...activeTrialSkinIds, skinId] });
        }
      },
      consumeTrials: () => {
        const {
          activeTrialSkinIds,
          consumedTrialSkinIds,
          pawnSkinId,
          diceSkinId,
          baseSkinId,
          plateauxSkinId
        } = get();
        if (activeTrialSkinIds.length === 0) return;

        const newConsumed = [
          ...consumedTrialSkinIds,
          ...activeTrialSkinIds.filter((id) => !consumedTrialSkinIds.includes(id))
        ];

        // Reset any component currently using a trialed skin back to basics
        const updates: Partial<SkinState> = {
          activeTrialSkinIds: [],
          consumedTrialSkinIds: newConsumed
        };
        if (activeTrialSkinIds.includes(pawnSkinId)) updates.pawnSkinId = 'basics';
        if (activeTrialSkinIds.includes(diceSkinId)) updates.diceSkinId = 'basics';
        if (activeTrialSkinIds.includes(baseSkinId)) updates.baseSkinId = 'basics';
        if (activeTrialSkinIds.includes(plateauxSkinId)) updates.plateauxSkinId = 'basics';

        set(updates);
      }
    }),
    {
      name: 'ludoka-skin',
      version: 2,
      migrate: (persistedState, version) => {
        if (version === 0) {
          const old = persistedState as { activeSkinId?: SkinId };
          const id = old.activeSkinId ?? 'basics';
          return {
            pawnSkinId: id,
            diceSkinId: id,
            baseSkinId: id,
            plateauxSkinId: id,
            activeTrialSkinIds: [],
            consumedTrialSkinIds: []
          };
        }
        if (version === 1) {
          const old = persistedState as Partial<SkinState>;
          return {
            ...old,
            activeTrialSkinIds: [],
            consumedTrialSkinIds: []
          };
        }
        return persistedState as SkinState;
      }
    }
  )
);
